import type {
  BrandSpecials,
  SpecialQualifyingStatus,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import {
  SpecialDetailLayout,
  SpecialDetailStatus,
  allowDiscountsOnRequiredProducts,
  getDiscountLabel,
  getDiscountedWithoutSameQualified,
  getSpecialType as getSpecialTypeLabel,
  getStatusText,
} from '@iheartjane/menu';
import { Flex, Link, Modal, Typography } from '@iheartjane/reefer';
import type {
  Breadcrumb,
  ComplianceLanguageSettings,
  LabeledOption,
  Nullish,
} from '@iheartjane/types';
import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import { useFetcher, useNavigate } from 'react-router';

import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import {
  FilterSelector,
  MobileFiltersModal,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { CartAction } from '@jane/bloom/routes/api.cart-items/types';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  ProductApplicableIds,
  StoreSpecial,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getSpecialType } from '@jane/bloom/utils/specials/getSpecialType';
import {
  createBulkDealProductFilter,
  shouldShowWeight,
} from '@jane/bloom/utils/specials/shouldShowWeight';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { SpecialsViewModel } from '@jane/bloom/viewModels/specials';
import type { AlgoliaProduct } from '@jane/search/types';

import { BulkOrCartTotalProgress } from './bulkOrCartTotalProgress';
import { BundleProgress } from './bundleProgress';

import styles from './deal.module.css';

interface DealProps {
  applicablePriceIds: Nullish<ProductApplicableIds[]>;
  brandSpecials: BrandSpecials;
  breadcrumbs: Breadcrumb[];
  cart?: Cart;
  complianceLanguage: Nullish<ComplianceLanguageSettings>;
  currentSort: SortOptions;
  currentSpecial: StoreSpecial;
  customLanguage: CustomLanguage;
  janeGold: JaneGold;
  preloadedProductIds: number[];
  productSelected?: (product: ProductSearchResultViewModel) => void;
  rowInstance?: SmartSort<AlgoliaProduct>;
  sortOptions: LabeledOption<SortOptions>[];
  specialsProducts: SpecialsViewModel;
  statusData?: SpecialQualifyingStatus;
}

export const Deal = ({
  applicablePriceIds,
  brandSpecials,
  breadcrumbs,
  cart,
  complianceLanguage,
  currentSort,
  currentSpecial,
  customLanguage,
  janeGold,
  preloadedProductIds,
  rowInstance,
  sortOptions,
  specialsProducts,
  statusData,
}: DealProps) => {
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const weightFilter = useMemo(
    () =>
      shouldShowWeight({
        cartItems: cart?.items || [],
        currentSpecial,
        showOnlyWeights: applicablePriceIds,
        specialsProducts,
      }),
    [cart?.items, currentSpecial, specialsProducts, applicablePriceIds]
  );

  const productFilter = useMemo(
    () => createBulkDealProductFilter(currentSpecial, weightFilter),
    [currentSpecial, weightFilter]
  );

  const totalProductCount = useMemo(() => {
    if (!rowInstance) {
      return 0;
    }

    return rowInstance.totalProducts;
  }, [rowInstance]);

  const hasResults = useMemo(() => {
    if (!rowInstance) {
      return false;
    }

    return rowInstance.products.length > 0;
  }, [rowInstance]);

  const { photo, special_type: specialType } = currentSpecial;
  const specialPhoto = photo ? photo.urls.original : null;

  const { featureFlags } = useRootLoaderData();
  const specialsRedesign = featureFlags[FLAGS.c3SpecialsRedesignMenu];
  const extendedDealsLabelsEnabled = featureFlags[FLAGS.c1ExtendedDealsLabels];
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const isBulk = specialType === 'bulk_pricing';
  const isCartTotal = specialType === 'cart_total';
  const isProduct = specialType === 'product';
  const isSpendingThreshold = specialType === 'spending_threshold';

  const qualifyingStatus = statusData?.qualifying_status;

  const {
    calloutSubtext,
    calloutText,
    footerSubtext,
    footerText,
    stickyCallout,
    stickyCalloutSubtext,
    stickyText,
  } = getStatusText({
    currentSpecial,
    qualifyingStatus: statusData,
  });

  const allowQualifiersToBeDiscounted =
    allowDiscountsOnRequiredProducts(currentSpecial);

  const discountedWithoutSameQualified =
    getDiscountedWithoutSameQualified(statusData);

  const discountedProductsToShow = allowQualifiersToBeDiscounted
    ? discountedWithoutSameQualified
    : statusData?.discounted_products;

  const maxWeightOption =
    typeof statusData?.target_weight === 'number' &&
    typeof statusData?.product_weight === 'number'
      ? statusData.target_weight - statusData.product_weight
      : null;

  const handleRemoveProduct = (product: { id?: number; price_id?: string }) => {
    fetcher.submit(
      {
        intent: CartAction.DeleteItem,
        priceId: product.price_id ?? null,
        productId: product.id?.toString() ?? null,
      },
      {
        action: `/api/cart-items`,
        method: 'POST',
      }
    );
  };

  const handleCheckout = () => {
    navigate(`/checkout`);
  };

  const scrollToStatus = () => {
    document
      .getElementById('category-selector')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!rowInstance) {
    return <></>;
  }

  if (specialsRedesign) {
    return (
      <main className={styles['main__redesign']}>
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          className={styles['breadcrumbs__redesign']}
        />

        <SpecialDetailLayout px={0} isLoading={false}>
          <SpecialDetailLayout.Banner p={8} top="var(--header-height)">
            <SpecialDetailStatus.Sticky
              calloutText={stickyCallout}
              calloutSubtext={stickyCalloutSubtext}
              text={stickyText}
              progressInGrams={isBulk}
              qualifiedProductsTotal={
                isBulk
                  ? (statusData?.product_weight ?? undefined)
                  : (statusData?.product_price ?? undefined)
              }
              qualifyBySpendAmount={
                isSpendingThreshold || isCartTotal || isBulk
              }
              qualifyingStatus={qualifyingStatus}
              qualifyingTargetAmount={
                (isBulk
                  ? statusData?.target_weight
                  : statusData?.qualifying_threshold) ?? 0
              }
              onClick={scrollToStatus}
            />
          </SpecialDetailLayout.Banner>

          <SpecialDetailLayout.Summary headerHeight={80}>
            <SpecialDetailStatus>
              <SpecialDetailStatus.Header
                headerImageSrc={
                  currentSpecial.photo?.urls?.original ?? undefined
                }
                specialDescription={currentSpecial.description || ''}
                specialTitle={currentSpecial.title}
                specialType={getSpecialTypeLabel(
                  specialType,
                  complianceLanguage,
                  extendedDealsLabelsEnabled
                )}
                specialTerms={currentSpecial.terms || ''}
              />
              {!isProduct && (
                <SpecialDetailStatus.BundleProgress
                  allowQualifiersToBeDiscounted={allowQualifiersToBeDiscounted}
                  calloutSubtext={calloutSubtext}
                  calloutText={calloutText || ''}
                  discountedProducts={discountedProductsToShow || []}
                  isGold={false}
                  onRemoveProduct={handleRemoveProduct}
                  progressInGrams={isBulk}
                  qualifiedProducts={statusData?.qualified_products ?? []}
                  qualifiedProductsTotal={
                    isBulk
                      ? (statusData?.product_weight ?? undefined)
                      : (statusData?.product_price ?? undefined)
                  }
                  qualifyBySpendAmount={
                    isSpendingThreshold || isCartTotal || isBulk
                  }
                  qualifyingStatus={qualifyingStatus ?? 'not_qualified'}
                  qualifyingTargetAmount={
                    (isBulk
                      ? statusData?.target_weight
                      : statusData?.qualifying_threshold) ?? 0
                  }
                  tagLabel={getDiscountLabel({
                    discountAmount: currentSpecial.discount_amount,
                    discountType: currentSpecial.discount_type,
                    specialType,
                    storeComplianceLanguageSettings: complianceLanguage,
                  })}
                />
              )}
              {!isProduct && (
                <SpecialDetailStatus.Footer
                  footerText={footerText}
                  footerSubtext={footerSubtext}
                  onCheckoutClick={handleCheckout}
                  qualifyingStatus={qualifyingStatus ?? 'not_qualified'}
                />
              )}
            </SpecialDetailStatus>
          </SpecialDetailLayout.Summary>

          <SpecialDetailLayout.Content>
            <FilterSelector
              availableProductsCount={totalProductCount}
              sortOptions={sortOptions}
              currentSort={currentSort}
              hideLabeledFilters={true}
              hideSearch
              showCompare={featureFlags[FLAGS.c1CompareProducts]}
            />

            <MobileFiltersModal
              availableProductsCount={totalProductCount}
              sortOptions={sortOptions}
              currentSort={currentSort}
            />

            {!hasResults && (
              <Flex justifyContent="center" alignItems="center" height="200px">
                <Typography
                  color="inherit"
                  variant="h1-semibold"
                  data-testid="no-results"
                >
                  No products available
                </Typography>
              </Flex>
            )}

            <ProductGrid
              brandSpecials={brandSpecials}
              customLanguage={customLanguage}
              maxWeightOption={maxWeightOption}
              preloadedProductIds={preloadedProductIds}
              productFilter={productFilter}
              rowInstance={rowInstance}
              specials={[currentSpecial]}
              weightFilter={weightFilter}
              janeGold={janeGold}
            />
          </SpecialDetailLayout.Content>
        </SpecialDetailLayout>
      </main>
    );
  }

  return (
    <main className={styles['main']}>
      <ClientOnly>
        {() => (
          <Modal
            open={isTermsModalOpen}
            onRequestClose={() => setIsTermsModalOpen(false)}
            className={styles['termsModal']}
            overlayClose
          >
            <Modal.Header
              title="Terms and Conditions"
              textAlign="center"
              className={styles['termsModal__header']}
            />
            <Modal.Content className={styles['termsModal__content']}>
              <Typography color="text-main">{currentSpecial.terms}</Typography>
            </Modal.Content>
          </Modal>
        )}
      </ClientOnly>
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        className={styles['breadcrumbs']}
      />
      <div>
        {specialPhoto ? (
          <div className={styles['bannerContainer']}>
            <div className={styles['imgContainer']}>
              <img
                className={styles['img']}
                src={specialPhoto}
                alt={`${currentSpecial.title} special`}
              />
            </div>
            <div
              className={clsx(
                styles['detailsContainer'],
                styles['detailsContainer--withImage']
              )}
            >
              <Typography
                className={clsx(
                  styles['specialType'],
                  styles['specialType--withImage']
                )}
                color="inherit"
                variant="body-semibold"
              >
                {getSpecialType(
                  currentSpecial.special_type,
                  complianceLanguage,
                  extendedDealsLabelsEnabled
                )}
              </Typography>
              <Typography
                variant="h1-semibold"
                className={styles['detailsTitle']}
              >
                {currentSpecial.title}
              </Typography>

              {currentSpecial.terms && (
                <Link
                  onClick={() => setIsTermsModalOpen(true)}
                  className={styles['termsAndConditionsLink']}
                >
                  View Terms and Conditions
                </Link>
              )}

              <Typography className={styles['description']}>
                {currentSpecial.description}
              </Typography>
            </div>
          </div>
        ) : (
          <div className={styles['bannerContainer--noImg']}>
            <div className={styles['detailsContainer']}>
              <Typography
                className={clsx(styles['specialType'])}
                color="inherit"
                variant="body-semibold"
              >
                {getSpecialType(
                  currentSpecial.special_type,
                  complianceLanguage,
                  extendedDealsLabelsEnabled
                )}
              </Typography>
              <Typography
                variant="h1-semibold"
                className={styles['detailsTitle']}
              >
                {currentSpecial.title}
              </Typography>
              <div>
                {currentSpecial.terms && (
                  <Link
                    onClick={() => setIsTermsModalOpen(true)}
                    className={styles['termsAndConditionsLink']}
                  >
                    View Terms and Conditions
                  </Link>
                )}
                <Typography className={styles['description']}>
                  {currentSpecial.description}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {currentSpecial.special_type === 'bundle' && (
          <BundleProgress specialsProducts={specialsProducts} />
        )}

        {(currentSpecial.special_type === 'cart_total' ||
          currentSpecial.special_type === 'bulk_pricing') && (
          <BulkOrCartTotalProgress
            specialsProducts={specialsProducts}
            specialType={currentSpecial.special_type}
          />
        )}
      </div>

      <FilterSelector
        availableProductsCount={totalProductCount}
        sortOptions={sortOptions}
        currentSort={currentSort}
        hideLabeledFilters={true}
        hideSearch
        showCompare={featureFlags[FLAGS.c1CompareProducts]}
      />

      <div className={styles['dealsButtons']}>
        {currentSpecial.special_type !== 'bulk_pricing' && (
          <Typography
            color="inherit"
            data-testid="product-count"
            className={styles['productCount']}
          >
            {`${totalProductCount > 999 ? '1k+' : totalProductCount} products`}
          </Typography>
        )}
        <MobileFiltersModal
          availableProductsCount={totalProductCount}
          sortOptions={sortOptions}
          currentSort={currentSort}
        />
      </div>

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="200px">
          <Typography
            color="inherit"
            variant="h1-semibold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        customLanguage={customLanguage}
        preloadedProductIds={preloadedProductIds}
        productFilter={productFilter}
        rowInstance={rowInstance}
        specials={[currentSpecial]}
        weightFilter={weightFilter}
        janeGold={janeGold}
      />
    </main>
  );
};
